import Image from 'next/image';
import { Dialog, Transition } from '@headlessui/react';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import logoMobile from '@/public/images/reebelo_text_logo.svg';
import xIcon from '@/public/icons/menu/x.svg';
import logoWheelleft from '@/public/images/logo_wheel_left.svg';
import logoWheelright from '@/public/images/logo_wheel_right.svg';
import { CustomerFormType } from './types';
import CustomerModalHeader from './CustomerModalHeader';
import CognitoLoginForm from './CognitoLoginForm';
import CognitoRegisterForm from './CognitoRegisterForm';
import i18n from '@/settings/i18n';

const t = i18n.init('auth');

export default function CognitoCustomerModal(props: {
  isOpen: boolean;
  formType: CustomerFormType;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  additionalInfo?: string;
}) {
  const { isOpen, setIsOpen } = props;
  const [formType, setFormType] = useState<CustomerFormType>(props.formType);
  const panelRef = useRef<HTMLDivElement>(null);
  const ignoreClose = useRef(false);

  // The dialog closes on any click outside the panel. A text selection that
  // starts in the panel and is released over the backdrop is not a real
  // outside click, so ignore the close when the press began inside the panel.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element | null;

      ignoreClose.current = !!target && !!panelRef.current?.contains(target);
    };
    // Escape must always close, even mid-press.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        ignoreClose.current = false;
      }
    };
    const onMouseUp = () => {
      setTimeout(() => {
        ignoreClose.current = false;
      }, 0);
    };

    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mouseup', onMouseUp, true);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isOpen]);

  const onDialogClose = () => {
    if (ignoreClose.current) {
      return;
    }

    setIsOpen(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[70] text-gray-700"
        onClose={onDialogClose}
      >
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed left-1/2 top-0 z-10 mt-[18px] -translate-x-1/2 md:hidden">
            <Image src={logoMobile} loading="eager" alt="reebelo" />
          </div>
          <div className="fixed left-0 top-0 z-10 hidden lg:block">
            <Image
              height={104}
              width={208}
              alt="logoWheelleft"
              src={logoWheelleft}
            />
          </div>
          <div className="fixed bottom-0 right-0 z-10 hidden lg:block">
            <Image
              height={392}
              width={241}
              alt="logoWheelright"
              src={logoWheelright}
            />
          </div>
          <div className="fixed inset-0 bg-teal-500 md:bg-gray-700/50" />
        </Transition.Child>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          className="fixed inset-0 z-[70] flex w-full flex-col justify-start p-5 md:items-center md:justify-center"
        >
          <Dialog.Panel
            ref={panelRef}
            className="mx-auto mt-11 max-h-screen w-full max-w-[500px] overflow-auto rounded-md bg-white px-[2.5rem] py-[2rem] shadow-xl md:mt-0"
          >
            {/* Login renders its own close (X) on the title row to save space. */}
            {formType !== CustomerFormType.LOGIN && (
              <div className="flex items-center justify-end">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex h-10 w-10 items-center justify-center transition duration-150 ease-in-out hover:scale-105"
                >
                  <div className="h-4 w-4">
                    <Image
                      alt={t`Close`}
                      src={xIcon}
                      height={30}
                      width={30}
                      className="h-full w-full"
                    />
                  </div>
                </button>
              </div>
            )}
            <div>
              {props.additionalInfo && (
                <CustomerModalHeader header={props.additionalInfo} />
              )}
              {formType === CustomerFormType.LOGIN && (
                <CognitoLoginForm
                  setFormType={setFormType}
                  onSuccess={() => setIsOpen(false)}
                  onClose={() => setIsOpen(false)}
                />
              )}
              {formType === CustomerFormType.REGISTER && (
                <CognitoRegisterForm
                  setFormType={setFormType}
                  onSuccess={() => setIsOpen(false)}
                  onClose={() => setIsOpen(false)}
                />
              )}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
